/**
 * Centralizovana autorizacija i sanitizacija payload-a za obaveze (FE granica).
 * Backend ostaje izvor istine; ovo sprečava slučajno i namerno slanje zabranjenih polja sa klijenta.
 */

/**
 * @typedef {Object} AuthUser
 * @property {string|number} [id]
 * @property {string} [email]
 * @property {string} [access_token]
 * @property {string[]} [roles]
 * @property {object} [profile]
 * @property {object} [loginData]
 * @property {object} [loginData.profile]
 * @property {string[]} [loginData.roles]
 * @property {import('@/shared/notifications/notificationResolvers').LoginOrganizations} [loginData.organizations]
 */

/**
 * @typedef {Object} PermissionHelperOptions
 * @property {AuthUser|null|undefined} [user]
 * @property {boolean} [isAdmin]
 * @property {string} [saveContext]
 * @property {string} [requestedRole]
 */

import AuthService from '@/core/auth/AuthService';
import UserPreferencesService from '@/shared/services/UserPreferencesService';
import { REPORT_IMPLEMENTATION_STATUS_VALUE_SET } from '@/shared/utils/domain/reportImplementationStatus';

/** Prioritet JWT uloga za rezoluciju podrazumevanog `X-Role` header-a. */
const X_ROLE_PRIORITY = ['ADMIN', 'OGP_ADMIN', 'EVALUATOR', 'MODERATOR', 'TEAM_LEAD', 'CITIZEN'];

/**
 * JWT uloge sa ekvivalentnim privilegijama na `/admin` rutama.
 * @type {Set<string>}
 */
export const PLATFORM_ADMIN_ROLES = new Set(['ADMIN', 'OGP_ADMIN', 'EVALUATOR']);

/**
 * Proverava da li je naziv uloge jedna od platform admin uloga.
 * @param {string|number|null|undefined} role - Naziv ili ID uloge.
 * @returns {boolean} True ako je uloga u `PLATFORM_ADMIN_ROLES`.
 */
export const isAdminRoleName = (role) =>
  PLATFORM_ADMIN_ROLES.has(String(role ?? '').trim().toUpperCase());

/** Kod greške za zabranjene auth operacije nad obavezama. */
export const OBLIGATION_AUTH_ERROR_CODE = 'OBLIGATION_AUTH_FORBIDDEN';

/**
 * Greška autorizacije za obaveze — baca se kada FE granica odbije operaciju.
 */
export class ObligationAuthError extends Error {
  /**
   * @param {string} message - Poruka greške za korisnika.
   * @param {string} [code] - Kod greške (podrazumevano `OBLIGATION_AUTH_ERROR_CODE`).
   */
  constructor(message, code = OBLIGATION_AUTH_ERROR_CODE) {
    super(message);
    this.name = 'ObligationAuthError';
    this.code = code;
  }
}

/**
 * Učitava trenutno autentifikovanog korisnika iz AuthService-a.
 * Ako `loginData` nedostaje, dopunjava profil i uloge preko `AccountService.checkLogin()` (keš, bez duplog HTTP poziva ako je AuthContext već učitao podatke).
 * @param {typeof AuthService} [_authService] - Auth servis (podrazumevano globalni `AuthService`).
 * @returns {Promise<AuthUser|null>} User objekt ili `null` ako nije prijavljen.
 */
export async function getAuthUser(_authService = AuthService) {
  try {
    const user = (await _authService.getUser()) || null;
    if (!user) return null;
    if (user.loginData) return user;
    try {
      const AccountService = (await import('@/shared/services/AccountService')).default;
      const loginResponse = await AccountService.checkLogin();
      if (loginResponse) {
        return {
          ...user,
          loginData: {
            profile: loginResponse.profile || {},
            roles: loginResponse.roles || [],
            organizations: loginResponse.organizations || { requested: [], verified: [] },
          },
        };
      }
    } catch {
      /* enrichment neuspeh — vraćamo user bez loginData */
    }
    return user;
  } catch {
    return null;
  }
}

/**
 * Normalizuje JWT uloge korisnika u niz stringova (uppercase).
 * Podržava elemente kao string ili objekat sa `name` / `role` poljem.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @returns {string[]} Niz normalizovanih naziva uloga.
 */
export function normalizePlatformRoles(user) {
  const roles = user?.loginData?.roles || user?.roles || [];
  if (!Array.isArray(roles)) return [];
  return roles
    .map((role) => String(role?.name || role?.role || role || '').trim().toUpperCase())
    .filter(Boolean);
}

/**
 * Proverava da li korisnik ima rolu administratora platforme.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @returns {boolean} True ako je platform admin.
 */
export function isPlatformAdmin(user) {
  return normalizePlatformRoles(user).some((role) => PLATFORM_ADMIN_ROLES.has(role));
}

/**
 * Proverava da li korisnik ima određenu JWT ulogu.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @param {string} role - Naziv uloge (npr. `MODERATOR`, `CITIZEN`).
 * @returns {boolean} True ako uloga postoji u JWT-u.
 */
export function hasJwtRole(user, role) {
  return normalizePlatformRoles(user).includes(String(role ?? '').trim().toUpperCase());
}

/**
 * Proverava da li korisnik ima JWT ulogu `MODERATOR`.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @returns {boolean}
 */
export const isJwtModerator = (user) => hasJwtRole(user, 'MODERATOR');

/**
 * Proverava da li korisnik ima JWT ulogu `TEAM_LEAD`.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @returns {boolean}
 */
export const isJwtTeamLead = (user) => hasJwtRole(user, 'TEAM_LEAD');

/**
 * Poredi dva ID-ja (string ili broj) sa normalizacijom whitespace-a.
 * @param {string|number|null|undefined} a
 * @param {string|number|null|undefined} b
 * @returns {boolean}
 */
const sameId = (a, b) => {
  if (a == null || b == null) return false;
  return String(a).trim() === String(b).trim();
};

/**
 * Prikuplja sve kandidate za korisnički ID iz auth profila (bez email adresa).
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @returns {string[]} Jedinstveni ID kandidati.
 */
export function collectUserIdCandidates(user) {
  const list = [];
  const push = (v) => {
    if (v == null) return;
    const s = String(v).trim();
    if (!s || s.includes('@')) return;
    if (!list.some((x) => sameId(x, s))) list.push(s);
  };
  push(user?.loginData?.profile?.id);
  push(user?.id);
  push(user?.loginData?.profile?.sub);
  push(user?.profile?.['sub']);
  push(user?.sub);
  return list;
}

/**
 * Izdvaja normalizovanu email adresu korisnika iz OIDC profila.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @returns {string} Email (lowercase) ili prazan string.
 */
const resolveUserEmail = (user) =>
  (
    user?.loginData?.profile?.email ||
    user?.loginData?.profile?.['http://schema.id.rs/claims/email'] ||
    user?.profile?.email ||
    user?.profile?.['http://schema.id.rs/claims/email'] ||
    user?.email ||
    ''
  )
    .toLowerCase()
    .trim();

/**
 * Pronalazi unos moderatora koji odgovara korisniku (match po ID-u ili email-u).
 * Podržava raw BE oblik (`moderator_id`, `moderator_email`) i syncovani FE oblik (`id`, `email`).
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @param {Array<object>} [moderators] - Lista moderatora sa BE-a.
 * @returns {object|null}
 */
export function findUserModeratorEntry(user, moderators = []) {
  const mods = Array.isArray(moderators) ? moderators : [];
  if (!mods.length || !user) return null;
  const idCandidates = collectUserIdCandidates(user);
  const userEmail = resolveUserEmail(user);
  return (
    mods.find((mod) => {
      const modEmails = [
        (mod?.email || '').toLowerCase().trim(),
        (mod?.moderator_email || '').toLowerCase().trim(),
        (mod?.userName || '').toLowerCase().trim(),
      ].filter(Boolean);
      const modIds = [
        mod?.id,
        mod?.account_id,
        mod?.user_id,
        mod?.sub,
        mod?.moderator_id,
        mod?.userId,
      ].filter((x) => x != null && String(x).trim() !== '');
      const idHit = modIds.some((mid) => idCandidates.some((c) => sameId(c, mid)));
      const emailHit = Boolean(userEmail && modEmails.some((me) => me && me === userEmail));
      return idHit || emailHit;
    }) ?? null
  );
}

/**
 * Proverava da li je korisnik u listi moderatora obaveze (match po ID-u ili email-u).
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @param {Array<object>} [moderators] - Lista moderatora sa BE-a.
 * @returns {boolean}
 */
export function isUserInModeratorsList(user, moderators = []) {
  return findUserModeratorEntry(user, moderators) != null;
}

/**
 * Proverava da li je korisnik prihvaćeni moderator na listi (`moderator_accepted === true`).
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @param {Array<object>} [moderators] - Lista moderatora obaveze.
 * @returns {boolean}
 */
export function isAcceptedModeratorOnList(user, moderators = []) {
  const entry = findUserModeratorEntry(user, moderators);
  return Boolean(entry?.moderator_accepted);
}

/**
 * Proverava da li korisnik može da moderira obavezu
 * (JWT `MODERATOR` + prisutan u listi sa `moderator_accepted`).
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @param {Array<object>} [moderators] - Lista moderatora obaveze.
 * @returns {boolean}
 */
export function canModerateObligation(user, moderators = []) {
  return isJwtModerator(user) && isAcceptedModeratorOnList(user, moderators);
}

/** Statusi aktivnosti koje samo admin sme da postavi. */
const PLANNING_STATUSES_ADMIN_ONLY = new Set(['ACCEPTED', 'REJECTED']);

/** Dozvoljeni statusi activity proposal-a za ne-admina. */
const ACTIVITY_PROPOSAL_STATUSES_NON_ADMIN = new Set(['CREATED']);

/** Polja proposal odgovora rezervisana za admin ulogu. */
const PROPOSAL_ADMIN_ONLY_FIELDS = [
  'admin_agreed',
  'admin_comment',
  'admin_agreement_time',
];

/** Polja proposal odgovora rezervisana za moderator ulogu. */
const PROPOSAL_MODERATOR_ONLY_FIELDS = [
  'moderator_agreed',
  'moderator_comment',
  'moderator_agreement_time',
];

/**
 * Uklanja `status` obaveze iz save-or-update payload-a za ne-admina.
 * @param {Record<string, unknown>} payload - Payload za `POST /obligation/save-or-update`.
 * @param {{ user?: AuthUser|null, isAdmin?: boolean }} [options]
 * @returns {Record<string, unknown>} Sanitizovan payload.
 */
export function sanitizeObligationSavePayload(payload, options = {}) {
  if (!payload || typeof payload !== 'object') return payload;
  const admin = options.isAdmin ?? isPlatformAdmin(options.user);
  if (admin) return { ...payload };

  const cleaned = { ...payload };
  delete cleaned.status;
  return cleaned;
}

/**
 * Sanitizuje payload aktivnosti pre slanja na BE.
 * Za `proposal_cta` kontekst forsira `CREATED` status i `PROPOSER` tip kreatora.
 * @param {Record<string, unknown>} payload - Payload aktivnosti.
 * @param {{ user?: AuthUser|null, isAdmin?: boolean, saveContext?: string }} [options]
 * @returns {Record<string, unknown>} Sanitizovan payload.
 */
export function sanitizeActivitySavePayload(payload, options = {}) {
  if (!payload || typeof payload !== 'object') return payload;
  const admin = options.isAdmin ?? isPlatformAdmin(options.user);
  const saveContext = String(options.saveContext || '').trim();
  const out = { ...payload };

  if (saveContext === 'proposal_cta') {
    out.activity_status = 'CREATED';
    out.creator_participant_type = 'PROPOSER';
    return out;
  }

  if (admin) return out;

  const status = String(out.activity_status ?? '').trim().toUpperCase();
  if (status && REPORT_IMPLEMENTATION_STATUS_VALUE_SET.has(status)) {
    return out;
  }

  if (PLANNING_STATUSES_ADMIN_ONLY.has(status)) {
    out.activity_status = 'CREATED';
  }

  const creator = String(out.creator_participant_type ?? '').trim().toUpperCase();
  if (creator === 'ADMIN') {
    out.creator_participant_type = 'PROPOSER';
  }

  return out;
}

/**
 * Sanitizuje proposal response payload za ne-admina.
 * U `consensusPersist` režimu ne uklanja polja — BE očekuje oba agreement flaga i status pri usaglašavanju.
 * @param {Record<string, unknown>} payload - Proposal response payload.
 * @param {{ user?: AuthUser|null, isAdmin?: boolean, consensusPersist?: boolean }} [options]
 * @returns {Record<string, unknown>} Sanitizovan payload.
 */
export function sanitizeProposalResponsePayload(payload, options = {}) {
  if (!payload || typeof payload !== 'object') return payload;
  const admin = options.isAdmin ?? isPlatformAdmin(options.user);
  if (admin) return { ...payload };
  if (options.consensusPersist) return { ...payload };

  const out = { ...payload };
  let creator = String(out.creator_participant_type ?? '').trim().toUpperCase();
  if (creator === 'ADMIN') {
    out.creator_participant_type = 'PROPOSER';
    creator = 'PROPOSER';
  }

  for (const key of PROPOSAL_ADMIN_ONLY_FIELDS) {
    delete out[key];
  }

  if (creator === 'PROPOSER' || !creator) {
    delete out.obligation_proposal_status;
    for (const key of PROPOSAL_MODERATOR_ONLY_FIELDS) {
      delete out[key];
    }
  } else if (creator === 'MODERATOR') {
    const proposalStatus = String(out.obligation_proposal_status ?? '').trim().toUpperCase();
    if (proposalStatus === 'ACCEPTED') {
      delete out.obligation_proposal_status;
    }
  }

  return out;
}

/**
 * Sanitizuje payload za `POST /obligation/activity/proposal/save-or-update`.
 * Ne-admin ne sme postaviti status osim `CREATED` niti `ADMIN` kao tip kreatora.
 * @param {Record<string, unknown>} payload - Activity proposal payload.
 * @param {{ user?: AuthUser|null, isAdmin?: boolean }} [options]
 * @returns {Record<string, unknown>} Sanitizovan payload.
 */
export function sanitizeActivityProposalPayload(payload, options = {}) {
  if (!payload || typeof payload !== 'object') return payload;
  const admin = options.isAdmin ?? isPlatformAdmin(options.user);
  if (admin) return { ...payload };

  const out = { ...payload };
  const creator = String(out.creator_participant_type ?? '').trim().toUpperCase();
  if (creator === 'ADMIN') {
    out.creator_participant_type = 'PROPOSER';
  }

  const status = String(out.activity_proposal_status ?? '').trim().toUpperCase();
  if (status && !ACTIVITY_PROPOSAL_STATUSES_NON_ADMIN.has(status)) {
    out.activity_proposal_status = 'CREATED';
  }

  return out;
}

/**
 * Uklanja `status` polje iz `FormData` za ne-admina.
 * @param {FormData} formData - FormData za upload obaveze.
 * @param {{ user?: AuthUser|null, isAdmin?: boolean }} [options]
 * @returns {FormData} Sanitizovan FormData.
 */
export function sanitizeObligationFormData(formData, options = {}) {
  if (!(formData instanceof FormData)) return formData;
  const admin = options.isAdmin ?? isPlatformAdmin(options.user);
  if (admin) return formData;
  if (formData.has('status')) {
    formData.delete('status');
  }
  return formData;
}

/**
 * Proverava da li korisnik sme da menja status obaveze (`POST /obligation/status/update`).
 * Baca `ObligationAuthError` ako korisnik nije platform admin.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @throws {ObligationAuthError}
 */
export function assertCanUpdateObligationStatus(user) {
  if (!isPlatformAdmin(user)) {
    throw new ObligationAuthError(
      'Немате овлашћење за промену статуса обавезе. Контактирајте администратора.',
    );
  }
}

/**
 * Proverava da li je akcioni plan u `DRAFT` statusu.
 * @param {string|null|undefined} actionPlanStatus - Status akcionog plana.
 * @returns {boolean}
 */
export function isDraftActionPlan(actionPlanStatus) {
  return String(actionPlanStatus ?? '').trim().toUpperCase() === 'DRAFT';
}

/** Poruka kada korisnik nema ulogu za slanje predloga obaveze. */
export const OBLIGATION_PROPOSAL_NO_ROLE_MESSAGE =
  'Немате овлашћење да пошаљете предлог обавезе. Контактирајте администратора.';

/**
 * Proverava da li korisnik ima ulogu za slanje predloga obaveze.
 * BE `/obligation/save-or-update` prihvata samo `X-Role`: `ADMIN` ili `CITIZEN`.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @returns {boolean}
 */
export function hasObligationProposalSubmitRole(user) {
  if (isPlatformAdmin(user)) return true;
  return normalizePlatformRoles(user).includes('CITIZEN');
}

/**
 * Određuje `X-Role` za `POST /obligation/save-or-update`.
 * `CITIZEN` ima prednost nad `MODERATOR`.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @returns {'ADMIN'|'CITIZEN'|null}
 */
export function resolveObligationSaveXRole(user) {
  if (isPlatformAdmin(user)) return 'ADMIN';
  const roles = normalizePlatformRoles(user);
  if (roles.includes('CITIZEN')) return 'CITIZEN';
  return null;
}

/**
 * Proverava da li korisnik može da kreira predlog obaveze (JWT + DRAFT akcioni plan).
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @param {string|null|undefined} actionPlanStatus - Status akcionog plana.
 * @returns {boolean}
 */
export function canCreateObligationProposal(user, actionPlanStatus) {
  if (!user?.access_token) return false;
  if (!isDraftActionPlan(actionPlanStatus)) return false;
  return hasObligationProposalSubmitRole(user);
}

/**
 * Proverava da li korisnik sme da upravlja akcionim planom (`POST /action-plan/save-or-update`, promena statusa).
 * Baca `ObligationAuthError` ako korisnik nije platform admin.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @throws {ObligationAuthError}
 */
export function assertCanManageActionPlan(user) {
  if (!isPlatformAdmin(user)) {
    throw new ObligationAuthError(
      'Немате овлашћење за уређивање акционог плана. Контактирајте администратора.',
    );
  }
}

/**
 * Određuje podrazumevani `X-Role` HTTP header iz JWT uloga.
 * Prioritet: platform admin ili `consensusActor: 'admin'` → `ADMIN`; `moderator` actor → `MODERATOR`; zatim `X_ROLE_PRIORITY`; na kraju korisničke preference.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @param {{ consensusActor?: string }} [options] - `'admin'` | `'moderator'` | `'proposer'` | `'none'`
 * @returns {string} Vrednost za `X-Role` header.
 */
export function resolveApiXRoleHeader(user, options = {}) {
  const actor = String(options.consensusActor ?? '').trim().toLowerCase();
  if (isPlatformAdmin(user) || actor === 'admin') return 'ADMIN';
  if (actor === 'moderator') return 'MODERATOR';

  const roles = normalizePlatformRoles(user);
  for (const roleName of X_ROLE_PRIORITY) {
    if (roles.includes(roleName)) return roleName;
  }

  try {
    const preferences = UserPreferencesService.getPreferences();
    const selected = String(preferences?.selectedRole ?? '').trim().toUpperCase();
    if (selected && selected !== 'MODERATOR' && roles.includes(selected)) {
      return selected;
    }
  } catch {
    /* preference servis nedostupan */
  }

  return roles[0] || 'CITIZEN';
}

/**
 * Validira eksplicitni `X-Role` iz `options.headers` — odbija spoofing van JWT-a.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @param {string|null|undefined} requestedRole - Eksplicitno tražena uloga od callera.
 * @param {{ consensusActor?: string, allowFunctionalModerator?: boolean }} [options]
 * @returns {string} Validirana vrednost za `X-Role` header.
 */
export function resolveRequestedXRole(user, requestedRole, options = {}) {
  const fallback = resolveApiXRoleHeader(user, options);
  if (!requestedRole) return fallback;

  const requested = String(requestedRole).trim().toUpperCase();
  const roles = normalizePlatformRoles(user);

  if (roles.includes(requested)) return requested;
  if (requested === 'ADMIN' && isPlatformAdmin(user)) return 'ADMIN';
  if (requested === 'MODERATOR' && options.allowFunctionalModerator) return 'MODERATOR';

  return fallback;
}

/**
 * Određuje `X-Role` za `POST /accounts/organizations/request-access`.
 * Vraća `CITIZEN` samo ako je uloga prisutna u JWT-u.
 * @param {AuthUser|null|undefined} user - User objekt iz auth tokena.
 * @returns {'CITIZEN'|null}
 */
export function resolveAccountMembershipXRole(user) {
  const roles = normalizePlatformRoles(user);
  return roles.includes('CITIZEN') ? 'CITIZEN' : null;
}
